// 🌙 Venere Feature Flags System
// Easy enable/disable of experimental features

import { useEffect, useMemo, useState } from 'react';
import { useUserStore } from '../../../utils/stores/useUserStore';

// ============================================================================
// FEATURE FLAG TYPES
// ============================================================================

export interface FeatureFlag {
  id: string;
  name: string;
  description: string;
  enabled: boolean;
  category: 'ui' | 'functionality' | 'performance' | 'integration' | 'experimental';
  priority: 'low' | 'medium' | 'high' | 'critical';
  createdAt: Date;
  updatedAt: Date;
  createdBy: string;
  tags: string[];
  // A/B Testing
  abTest?: {
    enabled: boolean;
    variant: 'A' | 'B' | 'C';
    trafficPercentage: number;
  };
  // Gradual Rollout
  rollout?: {
    enabled: boolean;
    percentage: number;
    targetUsers?: string[];
  };
}

// ============================================================================
// FEATURE FLAGS CONFIGURATION
// ============================================================================

export const featureFlags: Record<string, FeatureFlag> = {
  // UI Experiments
  'venere-dark-mode-v2': {
    id: 'venere-dark-mode-v2',
    name: 'Dark Mode V2',
    description: 'Enhanced dark mode with better contrast and animations',
    enabled: true,
    category: 'ui',
    priority: 'medium',
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date('2024-01-01'),
    createdBy: 'venere-team',
    tags: ['ui', 'dark-mode', 'accessibility'],
  },

  'venere-animations-v2': {
    id: 'venere-animations-v2',
    name: 'Advanced Animations',
    description: 'Framer Motion powered animations throughout the app',
    enabled: true,
    category: 'ui',
    priority: 'medium',
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date('2024-01-01'),
    createdBy: 'venere-team',
    tags: ['ui', 'animations', 'framer-motion'],
  },

  'venere-glassmorphism': {
    id: 'venere-glassmorphism',
    name: 'Glassmorphism Effects',
    description: 'Glassmorphism UI effects for cards and modals',
    enabled: false,
    category: 'ui',
    priority: 'low',
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date('2024-01-01'),
    createdBy: 'venere-team',
    tags: ['ui', 'glassmorphism', 'visual-effects'],
  },

  // Functionality Experiments
  'venere-real-time-collaboration': {
    id: 'venere-real-time-collaboration',
    name: 'Real-time Collaboration',
    description: 'Real-time collaboration features using WebSockets',
    enabled: false,
    category: 'functionality',
    priority: 'high',
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date('2024-01-01'),
    createdBy: 'venere-team',
    tags: ['functionality', 'websockets', 'collaboration'],
  },

  'venere-voice-control': {
    id: 'venere-voice-control',
    name: 'Voice Control',
    description: 'Voice control interface using Web Speech API',
    enabled: false,
    category: 'functionality',
    priority: 'medium',
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date('2024-01-01'),
    createdBy: 'venere-team',
    tags: ['functionality', 'voice', 'accessibility'],
  },

  'venere-ai-assistant': {
    id: 'venere-ai-assistant',
    name: 'AI Assistant',
    description: 'AI-powered assistant for user guidance',
    enabled: false,
    category: 'functionality',
    priority: 'high',
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date('2024-01-01'),
    createdBy: 'venere-team',
    tags: ['functionality', 'ai', 'assistant'],
  },

  // Performance Experiments
  'venere-virtual-scrolling': {
    id: 'venere-virtual-scrolling',
    name: 'Virtual Scrolling',
    description: 'Virtual scrolling for large lists and data tables',
    enabled: false,
    category: 'performance',
    priority: 'medium',
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date('2024-01-01'),
    createdBy: 'venere-team',
    tags: ['performance', 'virtual-scrolling', 'optimization'],
  },

  'venere-webassembly': {
    id: 'venere-webassembly',
    name: 'WebAssembly Integration',
    description: 'WebAssembly for performance-critical computations',
    enabled: false,
    category: 'performance',
    priority: 'high',
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date('2024-01-01'),
    createdBy: 'venere-team',
    tags: ['performance', 'webassembly', 'computation'],
  },

  // Integration Experiments
  'venere-webgl-visualization': {
    id: 'venere-webgl-visualization',
    name: 'WebGL Visualization',
    description: '3D visualizations using WebGL and Three.js',
    enabled: false,
    category: 'integration',
    priority: 'medium',
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date('2024-01-01'),
    createdBy: 'venere-team',
    tags: ['integration', 'webgl', 'threejs', '3d'],
  },

  'venere-webrtc': {
    id: 'venere-webrtc',
    name: 'WebRTC Integration',
    description: 'Peer-to-peer communication using WebRTC',
    enabled: false,
    category: 'integration',
    priority: 'low',
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date('2024-01-01'),
    createdBy: 'venere-team',
    tags: ['integration', 'webrtc', 'p2p'],
  },

  // Experimental Features
  'venere-neural-ui': {
    id: 'venere-neural-ui',
    name: 'Neural UI',
    description: 'AI-generated UI components and layouts',
    enabled: false,
    category: 'experimental',
    priority: 'low',
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date('2024-01-01'),
    createdBy: 'venere-team',
    tags: ['experimental', 'ai', 'neural', 'ui-generation'],
  },

  'venere-holographic-ui': {
    id: 'venere-holographic-ui',
    name: 'Holographic UI',
    description: 'Holographic-style UI effects and interactions',
    enabled: false,
    category: 'experimental',
    priority: 'low',
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date('2024-01-01'),
    createdBy: 'venere-team',
    tags: ['experimental', 'holographic', 'visual-effects'],
  },
};

// ============================================================================
// FEATURE FLAG UTILITIES
// ============================================================================

export class FeatureFlagManager {
  private static instance: FeatureFlagManager;
  private flags: Record<string, FeatureFlag>;
  private userEmail?: string;

  private constructor() {
    this.flags = featureFlags;
  }

  static getInstance(): FeatureFlagManager {
    if (!FeatureFlagManager.instance) {
      FeatureFlagManager.instance = new FeatureFlagManager();
    }
    return FeatureFlagManager.instance;
  }

  // Set user email for user-specific flags
  setUserEmail(email: string) {
    this.userEmail = email;
  }

  // Check if a feature is enabled
  isEnabled(flagId: string): boolean {
    const flag = this.flags[flagId];
    if (!flag) {
      console.warn(`Feature flag "${flagId}" not found`);
      return false;
    }

    // Check if flag is globally enabled
    if (!flag.enabled) {
      return false;
    }

    // Check A/B testing
    if (flag.abTest?.enabled) {
      return this.isInABTestVariant(flag);
    }

    // Check gradual rollout
    if (flag.rollout?.enabled) {
      return this.isInRollout(flag);
    }

    return true;
  }

  // Get all enabled features
  getEnabledFeatures(): string[] {
    return Object.keys(this.flags).filter(flagId => this.isEnabled(flagId));
  }

  // Get features by category
  getFeaturesByCategory(category: FeatureFlag['category']): FeatureFlag[] {
    return Object.values(this.flags).filter(flag => flag.category === category);
  }

  // Get features by priority
  getFeaturesByPriority(priority: FeatureFlag['priority']): FeatureFlag[] {
    return Object.values(this.flags).filter(flag => flag.priority === priority);
  }

  // Enable/disable a feature
  toggleFeature(flagId: string, enabled: boolean): void {
    if (this.flags[flagId]) {
      this.flags[flagId].enabled = enabled;
      this.flags[flagId].updatedAt = new Date();
      console.log(`Feature flag "${flagId}" ${enabled ? 'enabled' : 'disabled'}`);
    }
  }

  // Add a new feature flag
  addFeatureFlag(flag: Omit<FeatureFlag, 'createdAt' | 'updatedAt'>): void {
    const newFlag: FeatureFlag = {
      ...flag,
      createdAt: new Date(),
      updatedAt: new Date(),
    };
    this.flags[flag.id] = newFlag;
    console.log(`Feature flag "${flag.id}" added`);
  }

  // Remove a feature flag
  removeFeatureFlag(flagId: string): void {
    if (this.flags[flagId]) {
      delete this.flags[flagId];
      console.log(`Feature flag "${flagId}" removed`);
    }
  }

  // A/B Testing logic
  private isInABTestVariant(flag: FeatureFlag): boolean {
    if (!flag.abTest || !this.userEmail) return false;
    
    // Simple hash-based assignment for consistent user experience
    const hash = this.hashString(this.userEmail);
    const percentage = flag.abTest.trafficPercentage;
    
    return (hash % 100) < percentage;
  }

  // Gradual rollout logic
  private isInRollout(flag: FeatureFlag): boolean {
    if (!flag.rollout) return false;
    
    // Check if user is in target list
    if (flag.rollout.targetUsers && this.userEmail) {
      return flag.rollout.targetUsers.includes(this.userEmail);
    }
    
    // Percentage-based rollout
    if (this.userEmail) {
      const hash = this.hashString(this.userEmail);
      return (hash % 100) < flag.rollout.percentage;
    }
    
    return false;
  }

  // Simple hash function for consistent user assignment
  private hashString(str: string): number {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return Math.abs(hash);
  }

  // Get feature flag details
  getFeatureFlag(flagId: string): FeatureFlag | undefined {
    return this.flags[flagId];
  }

  // Get all feature flags
  getAllFeatureFlags(): FeatureFlag[] {
    return Object.values(this.flags);
  }

  // Export flags for debugging
  exportFlags(): Record<string, FeatureFlag> {
    return { ...this.flags };
  }
}

// ============================================================================
// REACT HOOK FOR FEATURE FLAGS
// ============================================================================

export const useFeatureFlag = (flagId: string): boolean => {
  const [isEnabled, setIsEnabled] = useState(false);
  const { userData } = useUserStore();
  const manager = useMemo(() => FeatureFlagManager.getInstance(), []);

  useEffect(() => {
    if (userData?.email) {
      manager.setUserEmail(userData.email);
    }
    setIsEnabled(manager.isEnabled(flagId));
  }, [flagId, userData?.email, manager]);

  return isEnabled;
};

export const useFeatureFlags = (flagIds: string[]): Record<string, boolean> => {
  const [flags, setFlags] = useState<Record<string, boolean>>({});
  const { userData } = useUserStore();
  const manager = useMemo(() => FeatureFlagManager.getInstance(), []);

  useEffect(() => {
    if (userData?.email) {
      manager.setUserEmail(userData.email);
    }
    
    const enabledFlags: Record<string, boolean> = {};
    flagIds.forEach(flagId => {
      enabledFlags[flagId] = manager.isEnabled(flagId);
    });
    
    setFlags(enabledFlags);
  }, [flagIds, userData?.email, manager]);

  return flags;
};

// ============================================================================
// EXPORTS
// ============================================================================

export default FeatureFlagManager; 